import { lazy, FC, Suspense, useCallback } from 'react'
import { Route, Routes, Navigate, useNavigate } from 'react-router-dom'
import { MasterLayout } from '../../_metronic/layout/MasterLayout'
import TopBarProgress from 'react-topbar-progress-indicator'
import { DashboardWrapper } from '../pages/dashboard/DashboardWrapper'
import { MenuTestPage } from '../pages/MenuTestPage'
import { getCSSVariableValue } from '../../_metronic/assets/ts/_utils'
import { WithChildren } from '../../_metronic/helpers'
import BuilderPageWrapper from '../pages/layout-builder/BuilderPageWrapper'
import { IPListWrapper } from '../pages/IPList/IPList'
import { ManageTemplateWrapper } from '../pages/ManageTemplate/ManageTemplate'
import { SMSReportWrapper } from '../pages/smsReport/SMSReport'
import { SupportCenterWrapper } from '../pages/supportCenter/Support'
import { MyProfileWrapper } from '../pages/myProfile/MyProfile'
import { BlackListURLWrapper } from '../pages/blackList/BlackListURL'
import { RolesManagementWrapper } from '../pages/rolesManagement/RolesManagement'
import { UserManagementWrapper } from '../pages/userManagement/UserManagement'
import { BlackListIPWrapper } from '../pages/blackList/BlackListIP'
import OperatorDataWrapper from '../pages/operatorData/OperatorData'
import OperatorSummaryWrapper from '../pages/operatorData/OperatorSummary'
import { PaymentReportWrapper } from '../pages/paymentReport/PaymentReport'
import { InvoiceWrapper } from '../modules/apps/orderlist/Invoice'
import UrlVerification from '../pages/UrlVerification/UrlVerification'
import KYCVerificationWrapper from '../pages/kycVerification/KYCVerification'
import { SenderApprovalWrapper } from '../pages/senderApproval/SenderApproval'
import { featureFlags } from '../../config/countryConfig'
import { useAuth } from '../modules/auth'
import useInactivityTimeout from '../hooks/useInactivityTimeout'
import { useAuthPermissions } from '../modules/auth/core/permissions'
import { LayoutSplashScreen } from '../../_metronic/layout/core'

const PrivateRoutes = () => {
  const { auth, logout, checkPermission } = useAuth()
  const { isLoading: permissionsLoading } = useAuthPermissions(true)
  const ProfilePage = lazy(() => import('../modules/profile/ProfilePage'))
  const WizardsPage = lazy(() => import('../modules/wizards/WizardsPage'))
  const AccountPage = lazy(() => import('../modules/accounts/AccountPage'))
  const WidgetsPage = lazy(() => import('../modules/widgets/WidgetsPage'))
  const ChatPage = lazy(() => import('../modules/apps/chat/ChatPage'))
  const UsersPage = lazy(() => import('../modules/apps/user-management/UsersPage'))
  const nav = useNavigate()
  const handleInactivity = useCallback(() => {
    if (auth?.access_token) {
      logout()
      nav('/auth')
    }
  }, [auth, logout, nav])
  useInactivityTimeout(handleInactivity, 60 * 60 * 1000)

  if (permissionsLoading) return <LayoutSplashScreen />
  return (
    <Routes>
      <Route element={<MasterLayout />}>
        {/* Redirect to Dashboard after success login/registartion */}
        <Route path='auth/*' element={<Navigate to='/dashboard' />} />
        {/* Pages */}
        <Route path='dashboard' element={<DashboardWrapper />} />
        {featureFlags.urlMappedTemplates && checkPermission('Mapped URLs', 'can_read') && (
          <Route path='managetemplate' element={<ManageTemplateWrapper />} />
        )}
        {checkPermission('URL Verification', 'can_read') && <Route path='urlverification' element={<UrlVerification />} />}
        {checkPermission('Account Statistics', 'can_read') && <Route path='operatordata' element={<OperatorDataWrapper />} />}
        <Route path='operatorsummary' element={<OperatorSummaryWrapper />} />
        {checkPermission('Vendor List', 'can_read') && <Route path='iplist' element={<IPListWrapper />} />}
        {checkPermission('SMS Report', 'can_read') && <Route path='smsreport' element={<SMSReportWrapper />} />}
        {checkPermission('Support Center', 'can_read') && <Route path='support' element={<SupportCenterWrapper />} />}
        <Route path='blacklisturl' element={<BlackListURLWrapper />} />
        <Route path='blacklistip' element={<BlackListIPWrapper />} />
        {checkPermission('Roles Management', 'can_read') && <Route path='rolesmanagement' element={<RolesManagementWrapper />} />}
        {checkPermission('User Management', 'can_read') && <Route path='usermanagement' element={<UserManagementWrapper />} />}
        <Route path='myprofile' element={<MyProfileWrapper />} />
        <Route path='paymentreport' element={<PaymentReportWrapper />} />
        <Route path='invoice' element={<InvoiceWrapper />} />
        {featureFlags.kycVerification && (
          <Route path='kycverification' element={<KYCVerificationWrapper />} />
        )}
        {featureFlags.senderApproval && (
          <Route path='senderapproval' element={<SenderApprovalWrapper />} />
        )}

        <Route path='builder' element={<BuilderPageWrapper />} />
        <Route path='menu-test' element={<MenuTestPage />} />
        {/* Lazy Modules */}
        <Route
          path='crafted/pages/profile/*'
          element={
            <SuspensedView>
              <ProfilePage />
            </SuspensedView>
          }
        />
        <Route
          path='crafted/pages/wizards/*'
          element={
            <SuspensedView>
              <WizardsPage />
            </SuspensedView>
          }
        />
        <Route
          path='crafted/widgets/*'
          element={
            <SuspensedView>
              <WidgetsPage />
            </SuspensedView>
          }
        />
        <Route
          path='crafted/account/*'
          element={
            <SuspensedView>
              <AccountPage />
            </SuspensedView>
          }
        />
        <Route
          path='apps/chat/*'
          element={
            <SuspensedView>
              <ChatPage />
            </SuspensedView>
          }
        />
        <Route
          path='apps/user-management/*'
          element={
            <SuspensedView>
              <UsersPage />
            </SuspensedView>
          }
        />
        {/* Page Not Found */}
        <Route path='*' element={<Navigate to='/error/404' />} />
      </Route>
    </Routes>
  )
}

const SuspensedView: FC<WithChildren> = ({ children }) => {
  const baseColor = getCSSVariableValue('--bs-primary')
  TopBarProgress.config({
    barColors: {
      '0': baseColor,
    },
    barThickness: 1,
    shadowBlur: 5,
  })
  return <Suspense fallback={<TopBarProgress />}>{children}</Suspense>
}

export { PrivateRoutes }
